import { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

/**
 * Inline editable table cell - click to edit, blur/Enter to save
 * Supports: text, number, select
 */
export function EditableCell({ 
  value, 
  field, 
  type = "text", 
  options = null, // for select: [{value, label, className}]
  onSave, 
  className = "",
  displayRender = null, // custom render for display mode
  placeholder = "",
}) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (type === "text" && inputRef.current.select) {
        inputRef.current.select();
      }
    }
  }, [editing]);

  // Sync if value changes externally
  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const save = () => {
    setEditing(false);
    if (editValue !== value) {
      const finalValue = type === "number" ? (parseFloat(editValue) || 0) : editValue;
      onSave(field, finalValue);
    }
  };

  const cancel = () => {
    setEditing(false);
    setEditValue(value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") save();
    if (e.key === "Escape") cancel();
    if (e.key === "Tab") save();
  };

  // Select type
  if (options) {
    const current = options.find(o => o.value === value);
    return (
      <Select value={value || ''} onValueChange={(v) => { onSave(field, v); }}>
        <SelectTrigger className="h-7 rounded-lg text-xs border-0 bg-transparent hover:bg-muted/50 px-1 w-auto min-w-[80px]">
          {displayRender ? displayRender(value) : (
            <Badge className={`text-xs rounded-full ${current?.className || 'bg-muted text-foreground'}`}>
              {current?.label || value}
            </Badge>
          )}
        </SelectTrigger>
        <SelectContent>
          {options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }

  // Display mode
  if (!editing) {
    return (
      <div 
        onClick={() => setEditing(true)}
        className={`cursor-pointer rounded-lg px-1.5 py-0.5 min-h-[28px] flex items-center hover:bg-primary/5 hover:ring-1 hover:ring-primary/20 transition-all ${className}`}
        title="Click to edit"
      >
        {displayRender ? displayRender(value) : (
          <span className={value ? '' : 'text-muted-foreground/40 italic text-xs'}>{value || placeholder || 'Click to edit'}</span>
        )}
      </div>
    );
  }

  // Edit mode
  return (
    <Input
      ref={inputRef}
      type={type}
      value={editValue ?? ''}
      onChange={e => setEditValue(e.target.value)}
      onBlur={save}
      onKeyDown={handleKeyDown}
      className="h-7 rounded-lg text-sm px-1.5 py-0 border-primary/40 bg-primary/5 focus-visible:ring-1 focus-visible:ring-primary"
      step={type === "number" ? "0.01" : undefined}
    />
  );
}
